import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Landmark } from 'lucide-react'
import type { KpiTrends } from '@/types/analytics'
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
} from '@/features/dashboard/components/chart-theme'
import { monthLabel } from './chart-utils'

interface FloorPlanChartProps {
  trends: KpiTrends | undefined
  isLoading: boolean
}

// Floor plan interest as a share of total gross profit. Target <4%, red
// flag >7%.
export function FloorPlanChart({ trends, isLoading }: FloorPlanChartProps) {
  const chartData =
    trends?.floor_plan_pct.map((p) => ({ month: monthLabel(p.month), pct: p.pct })) ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Landmark className="h-4 w-4" /> Floor Plan Interest
        </CardTitle>
        <CardDescription>Share of gross profit — target &lt; 4%, red flag above 7%</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : chartData.length === 0 ? (
          <div className="text-muted-foreground flex h-[200px] items-center justify-center text-sm">
            No floor plan data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v) => `${Number(v).toFixed(1)}%`} />
              <ReferenceLine
                y={4}
                stroke="hsl(var(--success))"
                strokeDasharray="4 4"
                label={{
                  value: '4%',
                  position: 'insideTopRight',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
              <ReferenceLine y={7} stroke="hsl(var(--destructive))" strokeDasharray="4 4" />
              <Line
                type="monotone"
                dataKey="pct"
                name="Interest % of gross"
                stroke="hsl(var(--chart-3))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
